import { useHandler } from "@redotech/react-util/hook";
import { useMemo, useState } from "react";

export function useUndoRedo<T>(
  initialState: T,
  equalityFn: (a: T, b: T) => boolean,
) {
  const [history, setHistory] = useState<T[]>([initialState]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const canUndo = currentIndex > 0;
  const canRedo = currentIndex < history.length - 1;
  const currentState = history[currentIndex] || initialState;

  const undo = useHandler(() => {
    if (canUndo) {
      setCurrentIndex((prev) => prev - 1);
    }
  });

  const redo = useHandler(() => {
    if (canRedo) {
      setCurrentIndex((prev) => prev + 1);
    }
  });

  const setState = useHandler(
    (newState: T | ((prev: T) => T), options?: { skipHistory?: boolean }) => {
      const updatedState =
        typeof newState === "function"
          ? (newState as (prev: T) => T)(currentState)
          : newState;

      if (options?.skipHistory) {
        setHistory((prev) => {
          const newHistory = [...prev];
          newHistory[currentIndex] = updatedState;
          return newHistory;
        });
      } else {
        if (equalityFn(currentState, updatedState)) {
          return;
        }

        setHistory((prev) => {
          const newHistory = [...prev.slice(0, currentIndex + 1), updatedState];
          return newHistory;
        });
        setCurrentIndex((prev) => prev + 1);
      }
    },
  );

  const reset = useHandler((newState: T) => {
    setHistory([newState]);
    setCurrentIndex(0);
  });

  return useMemo(
    () => ({
      state: currentState,
      setState,
      undo,
      redo,
      canUndo,
      canRedo,
      reset,
    }),
    [currentState, setState, undo, redo, canUndo, canRedo, reset],
  );
}
